import { flexRender, Row, Table } from '@tanstack/react-table';
import { AnimatePresence } from 'motion/react';
import React, { FC, Fragment, ReactElement } from 'react';

import { isMessageType } from '@savgroup-front-common/types';

import { menuAnimation } from '../../../animation/menuAnimation';
import { SafeFormattedMessageWithoutSpread } from '../../../formatters';
import { useIsNewUiEnabled } from '../../../hooks/useIsNewUiEnabled';

import { $StyledTd, $StyledTr, $SubTr } from './DatatableCore.styles';

interface TableBodyProps<D> {
  table: Table<D>;
  SubComponent?: FC<{ row: Row<D> }>;
  isSubRowStyled?: boolean;
  isExpandedBackgroundEnabled?: boolean;
  onClickRow?: (row: any) => void;
}
const Body = <D = unknown,>({
  table,
  SubComponent,
  isSubRowStyled = true,
  isExpandedBackgroundEnabled = true,
  onClickRow,
}: TableBodyProps<D>): ReactElement => {
  const { rows } = table.getRowModel();
  const isNewUiEnabled = useIsNewUiEnabled();

  return (
    <AnimatePresence>
      {rows.map((row) => {
        const isExpanded = row.getIsExpanded();
        const visibleCells = row.getVisibleCells();
        const { subRows } = row;

        if (row.depth !== 0) {
          return <Fragment key={row.id} />;
        }

        return (
          <Fragment key={row.id}>
            <$StyledTr
              $isExpanded={isExpanded}
              $isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
              $isNewUiEnabled={isNewUiEnabled}
              $isCliquable={!!onClickRow}
              onClick={() => (onClickRow ? onClickRow(row) : undefined)}
              {...menuAnimation}
              // To handle middle click
              onMouseDown={(event: React.MouseEvent<HTMLTableRowElement>) => {
                if (event.button === 1 && onClickRow) {
                  onClickRow(row);
                }

                return;
              }}
            >
              {visibleCells.map((cell) => {
                const value = cell.getValue();

                const cellContext = cell.getContext();

                return (
                  <$StyledTd
                    key={cell.id}
                    $size={cell.column.getSize()}
                    $minSize={cell.column.columnDef.minSize}
                    $maxSize={cell.column.columnDef.maxSize}
                  >
                    {isMessageType(value) ? (
                      <SafeFormattedMessageWithoutSpread message={value} />
                    ) : (
                      flexRender(cell.column.columnDef.cell, cellContext)
                    )}
                  </$StyledTd>
                );
              })}
            </$StyledTr>
            {!SubComponent &&
              isExpanded &&
              isSubRowStyled &&
              subRows.map((subRow) => {
                return (
                  <$StyledTr key={subRow.id} {...menuAnimation}>
                    {subRow.getVisibleCells().map((cell) => {
                      const value = cell.getValue();
                      const cellContext = cell.getContext();

                      return (
                        <$StyledTd
                          key={cell.id}
                          $size={cell.column.getSize()}
                          $minSize={cell.column.columnDef.minSize}
                          $maxSize={cell.column.columnDef.maxSize}
                        >
                          {isMessageType(value) ? (
                            <SafeFormattedMessageWithoutSpread
                              message={value}
                            />
                          ) : (
                            flexRender(cell.column.columnDef.cell, cellContext)
                          )}
                        </$StyledTd>
                      );
                    })}
                  </$StyledTr>
                );
              })}
            {SubComponent && isExpanded && isSubRowStyled && (
              <$SubTr
                $isExpandedBackgroundEnabled={isExpandedBackgroundEnabled}
                $isNewUiEnabled={isNewUiEnabled}
                $isCliquable={!!onClickRow}
                onClick={() => (onClickRow ? onClickRow(row) : undefined)}
                {...menuAnimation}
              >
                <td colSpan={visibleCells.length}>
                  <SubComponent row={row} />
                </td>
              </$SubTr>
            )}

            {SubComponent && isExpanded && !isSubRowStyled && (
              <SubComponent row={row} />
            )}
          </Fragment>
        );
      })}
    </AnimatePresence>
  );
};

export default Body;
